import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingStorefrontIcon,
  TagIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import SupplierForm from '../../components/suppliers/SupplierForm';
import { useAppData } from '../../contexts/AppDataContext';
import SupplierDetail from '../../components/suppliers/SupplierDetail';
import SupplierNegotiations from '../../components/suppliers/SupplierNegotiations';
import SupplierPerformance from '../../components/suppliers/SupplierPerformance';
import SupplierContracts from '../../components/suppliers/SupplierContracts';

const SuppliersPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [suppliersPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState('list');
  // Récupération des fournisseurs depuis le contexte (localStorage)
  const { suppliers: contextSuppliers = [], fetchAllData, deleteSupplier } = useAppData();
  
  // Définition des tabs
  const tabs = [
    { id: 'list', name: 'Liste des fournisseurs' },
    { id: 'negotiations', name: 'Négociations' },
    { id: 'contracts', name: 'Contrats' },
    { id: 'performance', name: 'Performance' }
  ];

  // Handle the new supplier action
  const handleAddSupplier = () => {
    navigate('/suppliers/new');
  };

  // Handle the view details action
  const handleViewSupplier = (id) => {
    navigate(`/suppliers/${id}`);
  };

  // Handle the edit action
  const handleEditSupplier = (id) => {
    navigate(`/suppliers/${id}/edit`);
  };

  // Handle the delete action
  const handleDeleteSupplier = (id, name) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le fournisseur ${name} ?`)) {
      try {
        // Call the function to delete the supplier
        deleteSupplier(id).then(success => {
          if (success) {
            toast.success(`Fournisseur ${name} supprimé avec succès`);
            // Refresh the data to update the UI
            fetchAllData();
          } else {
            toast.error(`Erreur lors de la suppression du fournisseur ${name}`);
          }
        });
      } catch (error) {
        console.error('Error deleting supplier:', error);
        toast.error(`Erreur lors de la suppression: ${error.message}`);
      }
    }
  };
  
  // Données fournisseurs par défaut (utilisées uniquement si aucune donnée n'existe dans le contexte ou localStorage)
  const sampleSuppliers = [];

  // Choisir la liste finale de fournisseurs et s'assurer qu'elle est sauvegardée
  const suppliers = contextSuppliers.length ? contextSuppliers : sampleSuppliers;
  
  // Force refresh suppliers data and ensure it's saved to localStorage
  useEffect(() => {
    const checkAndSaveSuppliers = () => {
      // Check if we have suppliers in context
      if (contextSuppliers.length === 0) {
        // If no suppliers in context, save sample suppliers to localStorage
        if (suppliers.length > 0) {
          localStorage.setItem('stockpro_suppliers', JSON.stringify(suppliers));
          localStorage.setItem('suppliers', JSON.stringify(suppliers)); // Legacy key for compatibility
          console.log('Sample suppliers saved to localStorage with keys "stockpro_suppliers" and "suppliers":', suppliers.length);
        }
      } else {
        // If we have suppliers in context, make sure they're saved to localStorage
        localStorage.setItem('stockpro_suppliers', JSON.stringify(contextSuppliers));
        localStorage.setItem('suppliers', JSON.stringify(contextSuppliers)); // Legacy key for compatibility
        console.log('Context suppliers saved to localStorage:', contextSuppliers.length);
      }
      
      // Force refresh app data to update context
      fetchAllData();
    };
    
    // Run the check and save function
    checkAndSaveSuppliers();
    
    // Log current suppliers for debugging
    console.log('Current suppliers in context:', contextSuppliers);
    console.log('Current suppliers in localStorage (stockpro_suppliers):', 
      JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]').length);
    console.log('Current suppliers in localStorage (suppliers):', 
      JSON.parse(localStorage.getItem('suppliers') || '[]').length);
      
    // Set up an interval to periodically check and refresh suppliers
    const refreshInterval = setInterval(() => {
      console.log('Periodic suppliers refresh...');
      fetchAllData();
    }, 5000); // Check every 5 seconds
    
    // Clean up the interval when component unmounts
    return () => clearInterval(refreshInterval);
  }, [contextSuppliers, suppliers, fetchAllData]);

  // Extract unique categories for filter
  const categories = [...new Set(suppliers.map(supplier => supplier.category))];

  // Statistics
  const stats = [
    {
      title: 'Total Fournisseurs',
      value: suppliers.length,
      icon: BriefcaseIcon,
      color: 'from-indigo-600 to-blue-600',
    },
    {
      title: 'Fournisseurs Actifs',
      value: suppliers.filter(s => s.status === 'Active').length,
      icon: BuildingStorefrontIcon,
      color: 'from-green-600 to-emerald-600',
    },
    {
      title: 'Commandes Totales',
      value: suppliers.reduce((sum, s) => sum + s.orders, 0),
      icon: DocumentTextIcon,
      color: 'from-amber-500 to-orange-600',
    },
    {
      title: 'Temps de livraison moyen',
      value: Math.round(suppliers.reduce((sum, s) => sum + s.avgDeliveryTime, 0) / suppliers.length) + ' jours',
      icon: ClockIcon,
      color: 'from-blue-500 to-cyan-600',
    },
  ];

  // Categories distribution
  const categoryDistribution = categories.map(category => ({
    name: category,
    count: suppliers.filter(s => s.category === category).length,
    color: getCategoryColor(category)
  }));

  // Get color based on category
  function getCategoryColor(category) {
    const colorMap = {
      'Papeterie': 'from-blue-400 to-blue-500',
      'Informatique': 'from-indigo-400 to-indigo-500',
      'Fournitures': 'from-green-400 to-green-500',
      'Mobilier': 'from-amber-400 to-amber-500',
      'Imprimerie': 'from-red-400 to-red-500',
      'Électronique': 'from-purple-400 to-purple-500',
      'Entretien': 'from-cyan-400 to-cyan-500'
    };
    
    return colorMap[category] || 'from-gray-400 to-gray-500';
  }

  // Filter suppliers based on search term and filters
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = 
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.category.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesCategoryFilter = categoryFilter === 'all' || supplier.category === categoryFilter;
    const matchesStatusFilter = statusFilter === 'all' || supplier.status === statusFilter;
    
    return matchesSearch && matchesCategoryFilter && matchesStatusFilter;
  });

  // Pagination
  const indexOfLastSupplier = currentPage * suppliersPerPage;
  const indexOfFirstSupplier = indexOfLastSupplier - suppliersPerPage;
  const currentSuppliers = filteredSuppliers.slice(indexOfFirstSupplier, indexOfLastSupplier);
  const totalPages = Math.ceil(filteredSuppliers.length / suppliersPerPage);

  // Rendu des étoiles pour la notation
  const renderStarRating = (rating) => {
    const numericRating = parseFloat(rating);
    const safeRating = Number.isFinite(numericRating) && numericRating >= 0 ? numericRating : 0;
    const clampedRating = Math.min(safeRating, 5);
    const fullStars = Math.floor(clampedRating);
    const halfStar = clampedRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <StarIcon key={`full-${i}`} className="h-4 w-4 text-amber-400 fill-amber-400" />
        ))}
        {halfStar && <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400/50" />}
        {[...Array(emptyStars)].map((_, i) => (
          <StarIcon key={`empty-${i}`} className="h-4 w-4 text-amber-400/30" />
        ))}
        <span className="ml-1 text-white/70 text-sm">{clampedRating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Fournisseurs
              </h1>
              <p className="text-white/60 mt-2">
                Gestion des fournisseurs et leurs informations
              </p>
            </div>
            <button
              onClick={handleAddSupplier}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouveau fournisseur</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <nav className="flex space-x-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    activeTab === tab.id
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'list' && (
            <>
              {/* Search and Filters */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder="Rechercher un fournisseur..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Filters */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <TagIcon className="h-5 w-5 text-white/50" />
                      <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">Toutes les catégories</option>
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <FunnelIcon className="h-5 w-5 text-white/50" />
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="Active">Actif</option>
                        <option value="Inactive">Inactif</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Suppliers List */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                          Fournisseur
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                          Catégorie
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                          Notation
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                          Commandes
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {currentSuppliers.map((supplier, index) => (
                        <motion.tr 
                          key={supplier.id} 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + (index * 0.05) }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-indigo-600/20 to-blue-600/20 flex items-center justify-center text-blue-400 font-semibold">
                                {supplier.name.charAt(0)}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-white">{supplier.name}</div>
                                <div className="text-sm text-white/50">{supplier.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-white">{supplier.contact}</div>
                            <div className="flex items-center text-sm text-white/50 mt-1">
                              <PhoneIcon className="h-3 w-3 mr-1" />
                              <span>{supplier.phone}</span>
                            </div>
                            <div className="flex items-center text-sm text-white/50 mt-1">
                              <EnvelopeIcon className="h-3 w-3 mr-1" />
                              <span>{supplier.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 inline-flex text-sm leading-5 font-medium rounded-full bg-white/10 text-white">
                              {supplier.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {renderStarRating(supplier.rating)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              supplier.status === 'Active' 
                                ? 'bg-green-100/10 text-green-400' 
                                : 'bg-red-100/10 text-red-400'
                            }`}>
                              {supplier.status === 'Active' ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">
                            {supplier.orders}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={() => handleViewSupplier(supplier.id)}
                                className="text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleEditSupplier(supplier.id)}
                                className="text-indigo-400 hover:text-indigo-300 transition-colors"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {filteredSuppliers.length > 0 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="text-sm text-white/60">
                      Affichage de {indexOfFirstSupplier + 1} à {Math.min(indexOfLastSupplier, filteredSuppliers.length)} sur {filteredSuppliers.length} fournisseurs
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-2 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      <div className="flex items-center bg-white/5 px-4 rounded-lg text-white">
                        {currentPage} / {totalPages}
                      </div>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {/* Negotiations Tab */}
          {activeTab === 'negotiations' && (
            <SupplierNegotiations suppliers={suppliers} />
          )}

          {/* Price Comparison Tab removed */}

          {/* Contracts Tab */}
          {activeTab === 'contracts' && (
            <SupplierContracts suppliers={suppliers} />
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <SupplierPerformance suppliers={suppliers} />
          )}
        </div>
      } />
      
      {/* Routes pour la création et l'édition */}
      <Route path="/new" element={<SupplierForm />} />
      <Route path="/:id" element={<SupplierDetail />} />
      <Route path="/:id/edit" element={<SupplierForm />} />
    </Routes>
  );
};

export default SuppliersPage; 